// Container da tela de Provedores do WL — GET …/providers do
// contrato: seção "Canais" (card por canal do catálogo com select de
// provider + switch de ativo, writes otimistas), "Credenciais
// Zenvia" (só quando algum canal usa provider ZENVIA_*) e "Journeys
// Zenvia" (tabela + alerta de eventos ativos sem journey). Estados
// skeleton / error+retry seguem o padrão das demais telas.
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import { InfoItem } from "@/components/shared/detail-components"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"

import { useNotificationCenterWhiteLabelProviders } from "../hooks"
import { ProviderChannelCard } from "./provider-channel-card"
import { ZenviaCredentialsCard } from "./zenvia-credentials-card"
import { ZenviaJourneysSection } from "./zenvia-journeys-section"

const providersPrefix =
  "pages.notificationCenter.providersPage"

// Skeleton fiel ao layout: header + 3 cards de canal + 2 seções.
const ProvidersSkeleton = () => (
  <div
    data-testid="wl-providers-skeleton"
    className="space-y-6"
  >
    <Skeleton className="h-16 w-full rounded-xl" />
    {Array.from({ length: 3 }).map((_, index) => (
      <Skeleton
        key={`wl-providers-skeleton-channel-${index}`}
        className="h-20 w-full rounded-xl"
      />
    ))}
    <Skeleton className="h-56 w-full rounded-xl" />
    <Skeleton className="h-56 w-full rounded-xl" />
  </div>
)

export const WhiteLabelProviders = () => {
  const { t } = useTranslation()
  const { whiteLabelId } = useParams()
  const { data, isLoading, isError, refetch } =
    useNotificationCenterWhiteLabelProviders(
      whiteLabelId as string
    )

  // Credenciais/journeys Zenvia só fazem sentido quando algum canal
  // usa provider da família ZENVIA_*.
  const usesZenvia =
    data?.channels.some((channelProvider) =>
      channelProvider.provider?.startsWith("ZENVIA")
    ) ?? false

  return (
    <div className="space-y-6 pb-10">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">
          {t(`${providersPrefix}.title`)}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(`${providersPrefix}.subtitle`)}
        </p>
      </header>

      {isLoading && <ProvidersSkeleton />}

      {!isLoading && isError && (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
          <p className="text-center text-sm text-red-700">
            {t(`${providersPrefix}.loadError`)}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => refetch()}
          >
            {t(`${providersPrefix}.retry`)}
          </Button>
        </div>
      )}

      {!isLoading && !isError && data && (
        <div className="space-y-8">
          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <InfoItem
              label={t(
                `${providersPrefix}.whiteLabelIdLabel`
              )}
              value={data.whiteLabelId}
              copyable
              truncate
            />
          </div>

          {/* Seção "Canais": card por canal do contrato. */}
          <section className="space-y-3">
            <h2 className="text-base font-semibold">
              {t(`${providersPrefix}.channels.title`)}
            </h2>
            <ul className="flex flex-col gap-3">
              {data.channels.map((channelProvider) => (
                <ProviderChannelCard
                  key={channelProvider.channel}
                  whiteLabelId={data.whiteLabelId}
                  channelProvider={channelProvider}
                  catalog={data.catalog}
                />
              ))}
            </ul>
          </section>

          {usesZenvia && (
            <>
              <ZenviaCredentialsCard
                whiteLabelId={data.whiteLabelId}
                zenvia={data.zenvia}
              />
              <ZenviaJourneysSection
                whiteLabelId={data.whiteLabelId}
                zenvia={data.zenvia}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}
